import React, { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { DemoOptionsPanel, useDemoOptions } from './DemoOptionsPanel' /* DEMO ONLY */
import healthStyles from './BriefShell.module.css'
import Moment from 'react-moment'
import { Navigate, Route, Routes, useParams, useLocation, Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { AccountDataUtil, AccountDetails, HostData, HostDataUtil, MaintenanceAndSupportStatus } from '../Customer/Data'
import { HostState } from '../../redux/hosts'
import { defaultAuthHandler } from '../../auth'
import {
  CalendarClockIcon,
  CurrencyIcon,
  EarthIcon,
  LicenseManagerIcon,
  LifecycleIcon,
  NotesIcon,
  OfficeBuildingIcon,
  OfficePrinterIcon,
  PaperCutOutlineIcon,
  FeatureUtilizationSectionIcon,
  FeaturesUnderutilizedIcon,
  PaperDocumentIcon,
  KeyInsightsIcon,
  ChatbotSparklesIcon,
  LightbulbIcon,
  SecurityOfficerIcon,
} from '../Icons/Icons'
import { BriefsHub } from './BriefsHub'
import { AdminToolsTab } from './AdminToolsTab'
import { useLatestProductOrders } from './LatestProductOrders'
import { LoadingDots } from '../LoadingDots/LoadingDots'
import RouteTabLink from '../Tab/RouteTabLink'
import { AiIconGradient } from './AiIconGradient'
import { LIFECYCLE_STAGES, lifecycleStageFor, stageOrDefault } from './lifecycle'
import { formatMoney, formatThousands } from './briefHelpers'
import { CustomerProfile, useCustomerProfile } from './useCustomerProfile'
import styles from './CustomerDetail.module.css'
import Toaster from '../Toaster'
import { Timestamps } from '../../service/Timestamps'
import AuthRole from '../../role'
import { ReactComponent as BriefIllustration } from '../../assets/img/brief-animated.svg'
import { ReactComponent as PartnershipIllustration } from '../../assets/img/partnership-animated.svg'
import { BriefThumb } from './BriefThumb'
import { FleetMakeupBriefing, HIGHLIGHT_ICONS, HIGHLIGHT_LABELS } from './FleetMakeupBrief'
import { useBriefData } from './useBriefData'
import { FleetValueFormula } from './FleetValueFormula'

export function nearestExpiry(account?: AccountDetails): Date | undefined {
  if (!account) return undefined
  const ms = AccountDataUtil.toDate(account.maintenanceAndSupportExpiryDate)
  const lic = AccountDataUtil.toDate(account.licenseExpiryDate)
  if (!ms && !lic) return undefined
  if (!ms) return lic
  if (!lic) return ms
  return ms < lic ? ms : lic
}

export function licenseLabel(account?: AccountDetails): string {
  if (!account) return ''
  const status = account.maintenanceAndSupportStatus
  if (status === MaintenanceAndSupportStatus.Current || status === MaintenanceAndSupportStatus.Expiring) {
    return 'M&S'
  }
  return account.licenseType || ''
}

const NotAvailable: FunctionComponent = () => (
  <span aria-label='Not available' title='No data available'>
    —
  </span>
)

interface DetailRowProps {
  icon: React.ReactNode
  label: string
  value?: React.ReactNode
  loading?: boolean
}

const DetailRow: FunctionComponent<DetailRowProps> = ({ icon, label, value, loading }) => (
  <div className={`flex ${styles.detailRow}`}>
    <div className='push flex-noshrink'>
      <i className='icon thirtytwo'>{icon}</i>
    </div>
    <div>
      <h5>{label}</h5>
      {loading ? (
        <div className={`p ${styles.detailLoading}`}>
          <LoadingDots />
        </div>
      ) : (
        <p>{value ?? <NotAvailable />}</p>
      )}
    </div>
  </div>
)

const fleetSizeText = (count?: number): string | undefined => {
  if (count === undefined || count === null) return undefined
  return `${count} ${count === 1 ? 'device' : 'devices'}`
}

interface PanelProps {
  teamID: string
  host: HostData
  hostState?: HostState
}

const CustomerDetailsPanel: FunctionComponent<PanelProps> = ({ teamID, host, hostState }) => {
  const fleetSize = fleetSizeText(hostState?.systemInfo?.embeddedCount)
  const accountExpiry = nearestExpiry(hostState?.accountDetails)
  const license = licenseLabel(hostState?.accountDetails)
  const { data: orders, loading: ordersLoading } = useLatestProductOrders(teamID, host.id)
  const bqExpiry = orders?.currentSupportExpiry ? new Date(orders.currentSupportExpiry) : undefined
  const expiry = accountExpiry ?? bqExpiry

  const { profile, loading: profileLoading } = useCustomerProfile(teamID, host.id)

  // A host is only linked if Multiverse has received systemInfo or accountDetails
  const isLinked = Boolean(hostState?.systemInfo && Object.keys(hostState.systemInfo).length > 0)

  const industryValue = profile?.industry || (
    <Link to={`/intelligence-hub/${host.id}/intel`}>Update organization profile</Link>
  )

  // as set on the Intel tab (Serve & upsell until one is set), with its dot as in the customer table
  const stage = lifecycleStageFor(stageOrDefault(profile?.lifecycleStage))
  const stageValue = stage && (
    <span className={styles.stageValue} data-stage={stage.value}>
      {stage.label}
    </span>
  )

  return (
    <div className='surface fh'>
      <header className={styles.surfaceHeader}>
        <h3>Summary</h3>
      </header>

      {!isLinked && (
        <div className={styles.unlinkedAlert}>
          <div className={styles.unlinkedAlertHeader}>
            <i className={styles.unlinkedAlertIcon}>
              <svg viewBox='0 0 24 24'>
                <path
                  d='m9.364 18.5 -0.932 0.932a4.5 4.5 0 0 1 -6.364 -6.364l4.773 -4.774a4.5 4.5 0 0 1 6.825 5.825'
                  fill='none'
                  stroke='currentColor'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                />
                <path
                  d='m14.818 5.567 0.75 -0.75a4.5 4.5 0 0 1 6.364 6.364l-4.773 4.773a4.5 4.5 0 0 1 -6.824 -5.826'
                  fill='none'
                  stroke='currentColor'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                />
              </svg>
            </i>
            <span>Multiverse connection required</span>
          </div>
          <div className={styles.unlinkedAlertMessage}>
            Size of fleet, Nearest expiry, and License details will populate once the customer's PaperCut environment is{' '}
            <Link to='/my-customers/list' className={styles.unlinkedAlertLink}>
              connected to Multiverse
            </Link>
            .
          </div>
        </div>
      )}

      <DetailRow icon={<OfficePrinterIcon />} label='Size of fleet' value={fleetSize} />
      <DetailRow
        icon={<EarthIcon />}
        label='Environment'
        value={host.infrastructure ? HostDataUtil.getInfrastructureShortName(host.infrastructure) : undefined}
      />
      <DetailRow
        icon={<PaperCutOutlineIcon />}
        label='Product'
        value={host.product ? HostDataUtil.getProductText(host.product) : undefined}
      />
      <DetailRow
        icon={<CalendarClockIcon />}
        label='Nearest expiry'
        loading={!accountExpiry && ordersLoading}
        value={expiry && <Moment format='D MMM YYYY' date={expiry} />}
      />
      <DetailRow icon={<LifecycleIcon />} label='Lifecycle stage' loading={profileLoading} value={stageValue} />
      <DetailRow icon={<OfficeBuildingIcon />} label='Industry' value={industryValue} />
      <DetailRow icon={<LicenseManagerIcon />} label='License' value={license || undefined} />
    </div>
  )
}

interface RenewalRisk {
  estimatedFleetValue?: number
  currencyCode?: string
  risk?: string
}

interface OverviewMetrics {
  estimatedFleetValue?: number
  currencyCode?: string
  risk?: string
  featuresUnderutilized?: number
  totalPagePrints?: number
}

interface HealthCheckBriefingResponse {
  insights?: string[]
  createdAt?: string
  data?: {
    sections?: {
      id: string
      metrics?: any[]
      notes?: { id: string; renewalRisk?: RenewalRisk }[]
      featureUtilization?: {
        categories?: {
          id: string
          features: { id: string; active: boolean }[]
        }[]
      }
      printQueues?: {
        queues?: { totalPages: number }[]
      }
      devices?: {
        devices?: { totalPages: number }[]
      }
    }[]
  }
}

interface OverviewTabProps {
  teamID: string
  host: HostData
}

const formatCurrency = (val: number | undefined, currencyCode: string | undefined): string => {
  if (val === undefined || !Number.isFinite(val)) return '—'
  return formatMoney(val, currencyCode)
}

// Status Evaluation Helpers
type StatStatus = 'cold' | 'warm' | 'hot'

const getRiskStat = (risk?: string): StatStatus => {
  if (!risk) return 'cold'
  const normalized = risk.trim().toLowerCase()
  if (normalized.includes('high') || normalized.includes('severe') || normalized.includes('critical')) {
    return 'hot'
  }
  if (normalized.includes('med') || normalized.includes('moderate')) {
    return 'warm'
  }
  return 'cold'
}

const getFleetValueStat = (value?: number): StatStatus => {
  if (value === undefined || value <= 0) return 'cold'
  if (value >= 50000) return 'hot'
  if (value >= 10000) return 'warm'
  return 'cold'
}

const getPrintVolumeStat = (pages?: number): StatStatus => {
  if (pages === undefined || pages <= 0) return 'cold'
  if (pages >= 100000) return 'hot'
  if (pages >= 10000) return 'warm'
  return 'cold'
}

/* a brief's four headline numbers as placeholders, the same size as the real ones so nothing shifts */
const BriefStatsSkeleton: FunctionComponent<{ label: string }> = ({ label }) => (
  <div className={styles.latestBriefStats} aria-busy='true' data-testid='latest-brief-loading'>
    {[0, 1, 2, 3].map(i => (
      <div key={i} className={`surface ${healthStyles.metricSurface}`} aria-hidden='true'>
        <div className={`flex flex-nowrap ${styles.skeletonTile}`}>
          <span className={`${styles.skeleton} ${styles.skeletonIcon}`} />
          <div className={styles.skeletonLines}>
            <span className={`${styles.skeleton} ${styles.skeletonValue}`} />
            <span className={`${styles.skeleton} ${styles.skeletonLabel}`} />
          </div>
        </div>
      </div>
    ))}
    <span className={styles.srOnly}>{label}</span>
  </div>
)

/* DEMO ONLY: for a brief with no headline numbers yet, the numbers it's expected to have, each with a dash, so its
   page of the overview's brief card has the same shape as the others */
interface ExpectedStat {
  label: string
  Icon: FunctionComponent
}

const ExpectedStats: FunctionComponent<{ stats: ExpectedStat[] }> = ({ stats }) => (
  <div className={styles.latestBriefStats} data-testid='expected-stats'>
    {stats.map(({ label, Icon }) => (
      <div key={label} className={`surface ${healthStyles.metricSurface}`}>
        <div className='flex flex-nowrap'>
          <i className='icon thirtytwo flex-noshrink push'>
            <Icon />
          </i>
          <div>
            <div className={healthStyles.tileValue} data-value-size='sm'>
              —
            </div>
            <h5>{label}</h5>
          </div>
        </div>
      </div>
    ))}
  </div>
)

const FLEET_MAKEUP_EXPECTED: ExpectedStat[] = [
  { label: 'Total device count', Icon: OfficePrinterIcon },
  { label: 'Printer count', Icon: OfficePrinterIcon },
  { label: 'End-of-life devices', Icon: HIGHLIGHT_ICONS['eol-devices'] ?? OfficePrinterIcon },
  { label: 'Embedded devices', Icon: OfficePrinterIcon },
]

const SECURITY_EXPECTED: ExpectedStat[] = [
  { label: 'PaperCut version', Icon: PaperCutOutlineIcon },
  { label: 'Releases behind latest', Icon: CalendarClockIcon },
  { label: 'Server platform', Icon: OfficeBuildingIcon },
  { label: 'Security features off', Icon: SecurityOfficerIcon },
]

/* DEMO ONLY: the Fleet makeup, as a page of the overview's brief card (when all briefs are shown): its
   headline counts and how many devices run embedded apps, in the same layout as the Health check's page */
const FleetMakeupOverview: FunctionComponent<{ teamID: string; hostID: string; chevron: React.ReactNode }> = ({
  teamID,
  hostID,
  chevron,
}) => {
  const { briefing, loading } = useBriefData<FleetMakeupBriefing>({
    teamID,
    customerID: hostID,
    briefType: 'fleet-makeup',
  })
  const metrics = briefing?.data?.sections?.find(section => section.id === 'highlights')?.metrics ?? []
  const tiles = metrics
    .map(metric => {
      if (metric.kind === 'count') {
        return {
          key: metric.id,
          value: metric.count.hasData ? formatThousands(metric.count.value) : '—',
          label: HIGHLIGHT_LABELS[metric.id] ?? metric.id,
          Icon: HIGHLIGHT_ICONS[metric.id] ?? OfficePrinterIcon,
        }
      }
      const embedded = metric.composition.segments.find(segment => segment.label === 'Embedded')
      return embedded
        ? { key: metric.id, value: formatThousands(embedded.count), label: 'Embedded devices', Icon: OfficePrinterIcon }
        : undefined
    })
    .filter((tile): tile is NonNullable<typeof tile> => Boolean(tile))
    .slice(0, 4)

  return (
    <Link
      to={`/intelligence-hub/${hostID}/briefs/fleet-makeup`}
      className={`surface ${styles.clickableCard} ${styles.latestBrief} ${styles.briefPage}`}
    >
      <header className={styles.latestBriefHeader}>
        <figure className={styles.latestBriefFigure}>
          <span className={styles.latestBriefPanel}>
            <BriefThumb brief='fleet-makeup' className={styles.latestBriefThumb} />
          </span>
        </figure>
        <div className={styles.latestBriefHeading}>
          <h3 className={styles.cardHeaderTitle}>
            <span>Fleet makeup</span>
            {chevron}
          </h3>
          <p className='no-margin-bottom'>
            What devices and print queues make up this customer's fleet, and where to refresh or expand.
          </p>
        </div>
        {briefing?.createdAt && (
          <small className={styles.latestBriefDate}>Generated {Timestamps.format(new Date(briefing.createdAt))}</small>
        )}
      </header>
      {loading && <BriefStatsSkeleton label='Loading the Fleet makeup' />}
      {!loading && tiles.length === 0 && <ExpectedStats stats={FLEET_MAKEUP_EXPECTED} />}
      {!loading && tiles.length > 0 && (
        <div className={styles.latestBriefStats}>
          {tiles.map(({ key, value, label, Icon }) => (
            <div key={key} className={`surface ${healthStyles.metricSurface}`}>
              <div className='flex flex-nowrap'>
                <i className='icon thirtytwo flex-noshrink push'>
                  <Icon />
                </i>
                <div>
                  <div className={healthStyles.tileValue} data-value-size='sm'>
                    {value}
                  </div>
                  <h5>{label}</h5>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Link>
  )
}

/* DEMO ONLY: the Security brief, as a page of the overview's brief card. It has no headline numbers yet, so it
   shows the ones it's expected to have, with dashes */
const SecurityOverview: FunctionComponent<{ teamID: string; hostID: string; chevron: React.ReactNode }> = ({
  teamID,
  hostID,
  chevron,
}) => {
  const { briefing, loading } = useBriefData<{ createdAt: string }>({
    teamID,
    customerID: hostID,
    briefType: 'security',
  })

  return (
    <Link
      to={`/intelligence-hub/${hostID}/briefs/security`}
      className={`surface ${styles.clickableCard} ${styles.latestBrief} ${styles.briefPage}`}
    >
      <header className={styles.latestBriefHeader}>
        <figure className={styles.latestBriefFigure}>
          <span className={styles.latestBriefPanel}>
            <BriefThumb brief='security' className={styles.latestBriefThumb} />
          </span>
        </figure>
        <div className={styles.latestBriefHeading}>
          <h3 className={styles.cardHeaderTitle}>
            <span>Security</span>
            {chevron}
          </h3>
          <p className='no-margin-bottom'>
            Security posture of this customer's PaperCut deployment, starting with version and platform.
          </p>
        </div>
        {briefing?.createdAt && (
          <small className={styles.latestBriefDate}>Generated {Timestamps.format(new Date(briefing.createdAt))}</small>
        )}
      </header>
      {loading ? (
        <BriefStatsSkeleton label='Loading the Security brief' />
      ) : (
        <ExpectedStats stats={SECURITY_EXPECTED} />
      )}
    </Link>
  )
}

/* DEMO ONLY: the briefs the overview's card can page through: the latest three at most */
const MAX_OVERVIEW_BRIEFS = 3
const OVERVIEW_BRIEFS = ['Health check', 'Fleet makeup', 'Security'].slice(0, MAX_OVERVIEW_BRIEFS)

const OverviewTab: FunctionComponent<OverviewTabProps> = ({ teamID, host }) => {
  const { options } = useDemoOptions() /* DEMO ONLY */
  const [metrics, setMetrics] = useState<OverviewMetrics | null>(null)
  const [insights, setInsights] = useState<string[]>([]) /* DEMO ONLY */
  const [generatedAt, setGeneratedAt] = useState<string | undefined>()
  const [loading, setLoading] = useState(true)
  // Bumping a card's key remounts its inline SVG, which restarts the CSS animations from the top.
  const [intelReplay, setIntelReplay] = useState(0)
  const [briefsReplay, setBriefsReplay] = useState(0)
  /* DEMO ONLY: with all briefs shown, which one the card is on */
  const paged = options.showAllOverviewBriefs
  const [briefPage, setBriefPage] = useState(0)
  const page = paged ? briefPage : 0

  useEffect(() => {
    const controller = new AbortController()

    setLoading(true)
    setMetrics(null)
    setInsights([])
    const request = axios.post<HealthCheckBriefingResponse>(
      `/api/teams/${teamID}/customers/${host.id}/briefs/health-check/generate`,
      null,
      { signal: controller.signal }
    )

    if (request && typeof request.then === 'function') {
      request
        .then(response => {
          const rawInsights = response?.data?.insights || []
          setInsights(rawInsights)
          setGeneratedAt(response?.data?.createdAt)

          const sections = response?.data?.data?.sections || []
          const renewalNote = sections
            .find(section => section.id === 'highlights')
            ?.notes?.find(note => note.id === 'renewal-risk')

          // Features underutilized count
          const featSection = sections.find(s => s.id === 'feature-utilization')
          let underutilizedCount = 0
          if (featSection?.featureUtilization?.categories) {
            featSection.featureUtilization.categories.forEach(cat => {
              cat.features?.forEach(f => {
                if (!f.active) underutilizedCount += 1
              })
            })
          }

          sections.forEach(s => {
            s.metrics?.forEach((m: any) => {
              if (m.id === 'features-underutilized' && m.kind === 'count') {
                underutilizedCount = m.count?.value ?? underutilizedCount
              }
            })
          })

          // Total page prints
          const queueSection = sections.find(s => s.id === 'print-queues')
          let pages = 0
          if (queueSection?.printQueues?.queues) {
            pages = queueSection.printQueues.queues.reduce((acc, q) => acc + (q.totalPages || 0), 0)
          } else {
            const deviceSection = sections.find(s => s.id === 'devices')
            if (deviceSection?.devices?.devices) {
              pages = deviceSection.devices.devices.reduce((acc, d) => acc + (d.totalPages || 0), 0)
            } else {
              sections.forEach(s => {
                s.metrics?.forEach((m: any) => {
                  if (m.kind === 'printVolume' && m.printVolume?.total?.current) {
                    pages = Math.max(pages, m.printVolume.total.current)
                  }
                })
              })
            }
          }

          setMetrics({
            estimatedFleetValue: renewalNote?.renewalRisk?.estimatedFleetValue,
            currencyCode: renewalNote?.renewalRisk?.currencyCode,
            risk: renewalNote?.renewalRisk?.risk,
            featuresUnderutilized: underutilizedCount,
            totalPagePrints: pages,
          })
          setLoading(false)
        })
        .catch(err => {
          if (!axios.isCancel(err)) {
            setLoading(false)
          }
        })
    } else {
      setLoading(false)
    }

    return () => {
      controller.abort()
    }
  }, [teamID, host.id])

  const ChevronRight = () => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      className={styles.chevronIcon}
    >
      <polyline points='9 18 15 12 9 6'></polyline>
    </svg>
  )

  const fallbackInsights = [
    'High renewal risk account driven by underutilized features and approaching M&S expiration.',
    'Consider initiating a proactive software optimization review with this customer.',
  ]
  const displayInsights = insights.length > 0 ? insights : fallbackInsights

  return (
    <div data-testid='overview-tab' className={styles.splitContent}>
      {/*
        Latest brief: a report-style summary of the most recent Health check, with its headline numbers
        laid out like the brief's own header, and a single way in.
      */}
      <div className={styles.briefPager}>
        {/* DEMO ONLY: a heading over the card, with arrows to page through the briefs (as on Portfolio insights) */}
        {paged && (
          <div className={styles.pagerHeader}>
            <h2 className={styles.pagerTitle}>Here&apos;s the latest on {host.name}</h2>
            <div className={styles.pagerNav} role='group' aria-label='Page through briefs'>
              <button
                type='button'
                className={styles.pagerButton}
                onClick={() => setBriefPage(page - 1)}
                disabled={page === 0}
                aria-label={`Previous brief${page > 0 ? `: ${OVERVIEW_BRIEFS[page - 1]}` : ''}`}
              >
                <svg
                  viewBox='0 0 24 24'
                  width='18'
                  height='18'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <polyline points='15 18 9 12 15 6' />
                </svg>
              </button>
              <button
                type='button'
                className={styles.pagerButton}
                onClick={() => setBriefPage(page + 1)}
                disabled={page === OVERVIEW_BRIEFS.length - 1}
                aria-label={`Next brief${page < OVERVIEW_BRIEFS.length - 1 ? `: ${OVERVIEW_BRIEFS[page + 1]}` : ''}`}
              >
                <svg
                  viewBox='0 0 24 24'
                  width='18'
                  height='18'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <polyline points='9 18 15 12 9 6' />
                </svg>
              </button>
            </div>
          </div>
        )}
        {page === 0 ? (
          <Link
            key='health-check'
            to={`/intelligence-hub/${host.id}/briefs/health-check`}
            className={`surface ${styles.clickableCard} ${styles.latestBrief}${paged ? ` ${styles.briefPage}` : ''}`}
          >
            <header className={styles.latestBriefHeader}>
              <figure className={styles.latestBriefFigure}>
                <span className={styles.latestBriefPanel}>
                  <BriefThumb brief='health-check' className={styles.latestBriefThumb} />
                </span>
              </figure>
              <div className={styles.latestBriefHeading}>
                <h3 className={styles.cardHeaderTitle}>
                  <span>Health check</span>
                  <ChevronRight />
                </h3>
                <p className='no-margin-bottom'>
                  The latest view of this customer's fleet, print volume, feature use and renewal risk.
                </p>
              </div>
              {generatedAt && (
                <small className={styles.latestBriefDate}>Generated {Timestamps.format(new Date(generatedAt))}</small>
              )}
            </header>

            {loading ? (
              <BriefStatsSkeleton label='Loading the latest brief' />
            ) : (
              <div className={styles.latestBriefStats}>
                <div
                  className={`surface ${healthStyles.metricSurface}`}
                  data-stat={getFleetValueStat(metrics?.estimatedFleetValue)}
                >
                  <div className='flex flex-nowrap'>
                    <i className='icon thirtytwo flex-noshrink push'>
                      <OfficePrinterIcon />
                    </i>
                    <div>
                      <div className={healthStyles.tileValue} data-value-size='sm'>
                        {formatCurrency(metrics?.estimatedFleetValue, metrics?.currencyCode)}
                      </div>
                      <h5>Estimated fleet value (per annum)</h5>
                    </div>
                  </div>
                </div>
                <div className={`surface ${healthStyles.metricSurface}`} data-stat={getRiskStat(metrics?.risk)}>
                  <div className='flex flex-nowrap'>
                    <i className='icon thirtytwo flex-noshrink push'>
                      <FeatureUtilizationSectionIcon />
                    </i>
                    <div>
                      <div className={healthStyles.tileValue} data-value-size='sm'>
                        {metrics?.risk ?? '—'}
                      </div>
                      <h5>Renewal risk</h5>
                    </div>
                  </div>
                </div>
                <div className={`surface ${healthStyles.metricSurface}`} data-stat='hot'>
                  <div className='flex flex-nowrap'>
                    <i className='icon thirtytwo flex-noshrink push'>
                      <FeaturesUnderutilizedIcon />
                    </i>
                    <div>
                      <div className={healthStyles.tileValue} data-value-size='sm'>
                        {metrics?.featuresUnderutilized ?? '—'}
                      </div>
                      <h5>Features underutilized</h5>
                    </div>
                  </div>
                </div>
                <div
                  className={`surface ${healthStyles.metricSurface}`}
                  data-stat={getPrintVolumeStat(metrics?.totalPagePrints)}
                >
                  <div className='flex flex-nowrap'>
                    <i className='icon thirtytwo flex-noshrink push'>
                      <PaperDocumentIcon />
                    </i>
                    <div>
                      <div className={healthStyles.tileValue} data-value-size='sm'>
                        {metrics?.totalPagePrints ? metrics.totalPagePrints.toLocaleString() : '—'}
                      </div>
                      <h5>Total page prints</h5>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* DEMO ONLY: AI key insights, as a simple list inside the same card */}
            {options.showOverviewKeyInsights && (
              <div className={styles.latestBriefInsights}>
                <h4 className={styles.latestBriefInsightsTitle}>
                  <i className='icon twenty flex-noshrink'>
                    <ChatbotSparklesIcon />
                  </i>
                  Key insights and suggestions
                </h4>
                {/* the lightbulbs take the AI gradient: these are AI-written */}
                <AiIconGradient />
                <ul>
                  {displayInsights.map(insight => (
                    <li key={insight}>
                      <i className='icon twenty outline flex-noshrink'>
                        <LightbulbIcon />
                      </i>
                      <span>{insight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Link>
        ) : page === 1 ? (
          <FleetMakeupOverview key='fleet-makeup' teamID={teamID} hostID={host.id} chevron={<ChevronRight />} />
        ) : (
          <SecurityOverview key='security' teamID={teamID} hostID={host.id} chevron={<ChevronRight />} />
        )}
      </div>

      <div className={styles.cardsRow}>
        {/* Intel Card */}
        <Link
          to={`/intelligence-hub/${host.id}/intel`}
          className={`surface ${styles.clickableCard}`}
          onMouseEnter={() => setIntelReplay(n => n + 1)}
        >
          <figure>
            <PartnershipIllustration key={intelReplay} aria-hidden='true' focusable='false' />
          </figure>
          <h3 className={styles.cardHeaderTitle}>
            <span>Intel</span>
            <ChevronRight />
          </h3>
          <p className='no-margin-bottom'>
            Add the organization profile, values and cost estimates. The more context you give, the sharper the briefs
            and Mission Control's answers.
          </p>
        </Link>

        {/* Briefs Card */}
        <Link
          to={`/intelligence-hub/${host.id}/briefs`}
          className={`surface ${styles.clickableCard}`}
          onMouseEnter={() => setBriefsReplay(n => n + 1)}
        >
          <figure>
            <BriefIllustration
              key={briefsReplay}
              className={styles.briefIllustration}
              aria-hidden='true'
              focusable='false'
            />
          </figure>
          <h3 className={styles.cardHeaderTitle}>
            <span>Briefs</span>
            <ChevronRight />
          </h3>
          <p className='no-margin-bottom'>
            Open briefs by lifecycle stage, like the Health check, with AI notes and Mission Control to explore them.
          </p>
        </Link>
      </div>
    </div>
  )
}

type RegenerationStatus = 'idle' | 'running' | 'complete' | 'failed'

interface IntelTabProps {
  customerID: string
}

/* Longest currency the value fields can show beside an amount (a code like AUD, a symbol, or a short name) */
const MAX_CURRENCY_LENGTH = 8

/* Company values are kept as a list; the textarea takes them separated by commas or new lines */
const parseCompanyValues = (text: string): string[] =>
  text
    .split(/[,\n]/)
    .map(value => value.trim())
    .filter(Boolean)

type ProfileField = keyof CustomerProfile
type ProfileErrors = Partial<Record<ProfileField, string>>

const isBlank = (v: unknown) => v === undefined || v === null || String(v).trim() === ''
const decimals = (v: string) => (v.includes('.') ? v.split('.')[1].length : 0)

/* Checks a money amount; returns an error message or undefined */
const checkAmount = (raw: unknown, label: string, maxDecimals: number, max: number): string | undefined => {
  if (isBlank(raw)) return undefined
  const v = String(raw).trim()
  if (!/^\d+(\.\d+)?$/.test(v))
    return `Enter ${label} as a number, without the currency symbol (e.g. ${maxDecimals > 2 ? '0.015' : '15.50'}).`
  if (decimals(v) > maxDecimals) return `Use no more than ${maxDecimals} decimal places.`
  if (parseFloat(v) > max) return `That looks too high for ${label}. Check the amount.`
  return undefined
}

export function validateProfile(profile: CustomerProfile): ProfileErrors {
  const errors: ProfileErrors = {}
  const thisYear = new Date().getFullYear()

  if (!isBlank(profile.website) && !/^(https?:\/\/)?[\w-]+(\.[\w-]+)+(\/\S*)?$/i.test(String(profile.website).trim())) {
    errors.website = 'Enter a web address, e.g. acme.com.'
  }
  if (!isBlank(profile.yearFounded)) {
    const y = String(profile.yearFounded).trim()
    if (!/^\d{4}$/.test(y) || Number(y) < 1800 || Number(y) > thisYear) {
      errors.yearFounded = `Enter a four-digit year between 1800 and ${thisYear}.`
    }
  }
  if (
    !isBlank(profile.numberOfEmployees) &&
    !/^\d[\d,]*(\s*[-–]\s*\d[\d,]*)?\+?$/.test(String(profile.numberOfEmployees).trim())
  ) {
    errors.numberOfEmployees = 'Enter a number or a range, e.g. 150 or 100-250.'
  }

  const deviceValue = checkAmount(profile.deviceValue, 'the value per device', 2, 1000000)
  const pageCostBW = checkAmount(profile.pageCostBW, 'the B&W page cost', 4, 10)
  const pageCostColor = checkAmount(profile.pageCostColor, 'the color page cost', 4, 10)
  if (deviceValue) errors.deviceValue = deviceValue
  if (pageCostBW) errors.pageCostBW = pageCostBW
  if (pageCostColor) errors.pageCostColor = pageCostColor

  const hasAmounts = [profile.deviceValue, profile.pageCostBW, profile.pageCostColor].some(v => !isBlank(v))
  const currency = String(profile.currencyCode || '').trim()
  if (hasAmounts && !currency) {
    errors.currencyCode = 'Enter the currency these amounts are in, e.g. AUD.'
  } else if (currency.length > MAX_CURRENCY_LENGTH) {
    errors.currencyCode = `Keep the currency to ${MAX_CURRENCY_LENGTH} characters or fewer, e.g. AUD or $.`
  }
  return errors
}

/* The lifecycle stage as a segmented toggle, filling its field. Its options share the width evenly and wrap onto
   more lines when they don't fit; once wrapped, the hairlines between options go (they can't line up across lines). */
const LifecycleStageToggle: FunctionComponent<{ value: string; onChange: (value: string) => void }> = ({
  value,
  onChange,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [wrapped, setWrapped] = useState(false)

  useLayoutEffect(() => {
    const toggle = ref.current
    if (!toggle) return
    const measure = () => {
      const options = toggle.children
      const first = options[0] as HTMLElement | undefined
      const last = options[options.length - 1] as HTMLElement | undefined
      setWrapped(Boolean(first && last && last.offsetTop > first.offsetTop))
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(toggle)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={styles.stageToggle}
      role='radiogroup'
      aria-labelledby='intel-lifecycle-stage'
      data-wrapped={wrapped || undefined}
    >
      {LIFECYCLE_STAGES.map(stage => {
        const selected = value === stage.value
        return (
          <button
            key={stage.value}
            id={`intel-stage-${stage.value}`}
            type='button'
            role='radio'
            aria-checked={selected}
            className={selected ? styles.stageSelected : styles.stageOption}
            data-stage={stage.value}
            onClick={() => onChange(stage.value)}
          >
            {stage.label}
          </button>
        )
      })}
    </div>
  )
}

const IntelTab: FunctionComponent<IntelTabProps> = ({ customerID }) => {
  const { options } = useDemoOptions() /* DEMO ONLY: how the fleet value formula reads */
  const [profile, setProfile] = useState<CustomerProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [regeneration, setRegeneration] = useState<RegenerationStatus>('idle')
  const [touched, setTouched] = useState<Partial<Record<ProfileField, boolean>>>({})
  const [submitted, setSubmitted] = useState(false)
  /* the company values as typed, so commas and spaces survive until the list is saved */
  const [companyValuesText, setCompanyValuesText] = useState<string | null>(null)
  const [bioStatus, setBioStatus] = useState<'idle' | 'generating' | 'generated'>('idle')
  const [bioBeforeGenerate, setBioBeforeGenerate] = useState('')

  const handleFieldChange = (field: keyof CustomerProfile, value: any) => {
    if (profile) {
      setProfile({ ...profile, [field]: value })
    }
  }

  const errors = profile ? validateProfile(profile) : {}
  /* show a field's error once it has been left, or after a save attempt */
  const errorFor = (field: ProfileField) => (touched[field] || submitted ? errors[field] : undefined)
  const blur = (field: ProfileField) => () => setTouched(prev => ({ ...prev, [field]: true }))
  const fieldProps = (field: ProfileField, id: string) => {
    const message = errorFor(field)
    return {
      onBlur: blur(field),
      'aria-invalid': message ? true : undefined,
      'aria-describedby': message ? `${id}-error` : undefined,
      className: message ? styles.inputInvalid : undefined,
    }
  }
  const FieldError: FunctionComponent<{ field: ProfileField; id: string }> = ({ field, id }) => {
    const message = errorFor(field)
    return message ? (
      <small id={`${id}-error`} className={styles.fieldError} role='alert'>
        {message}
      </small>
    ) : null
  }

  const handleSave = () => {
    if (!profile) return
    setSubmitted(true)
    if (Object.keys(validateProfile(profile)).length > 0) {
      Toaster.notifyFailure('Check the highlighted fields, then update again.')
      return
    }
    setSaving(true)

    const teamID = defaultAuthHandler.getTeamID()

    const payload = {
      ...profile,
      yearFounded: profile.yearFounded ? parseInt(String(profile.yearFounded), 10) || 0 : 0,
      deviceValue: profile.deviceValue ? parseFloat(String(profile.deviceValue)) || 0 : 0,
      pageCostBW: profile.pageCostBW ? parseFloat(String(profile.pageCostBW)) || 0 : 0,
      pageCostColor: profile.pageCostColor ? parseFloat(String(profile.pageCostColor)) || 0 : 0,
      lifecycleStage: stageOrDefault(profile.lifecycleStage),
    }

    axios
      .put<CustomerProfile>(`/api/teams/${teamID}/customers/${customerID}/profile`, payload)
      .then(response => {
        setProfile(response.data)
        setCompanyValuesText(null)
        setBioStatus('idle')
        Toaster.notifySuccess('Customer profile saved.')
        setSaving(false)
        if (AuthRole.hasPermission('editIntelligence')) {
          rebuildHealthCheckBrief(teamID)
        }
      })
      .catch(err => {
        Toaster.notifyFailure(err.message || 'An error occurred while saving the customer profile')
        setSaving(false)
      })
  }

  // Drafts a bio from the customer's website. It only fills the field: the partner reviews it, then saves.
  const generateBio = () => {
    if (!profile) return
    setBioStatus('generating')
    const teamID = defaultAuthHandler.getTeamID()
    axios
      .post<{ bio: string }>(`/api/teams/${teamID}/customers/${customerID}/profile/bio`)
      .then(response => {
        setBioBeforeGenerate(profile.bio || '')
        setProfile(current => (current ? { ...current, bio: response.data.bio } : current))
        setBioStatus('generated')
      })
      .catch(err => {
        setBioStatus('idle')
        Toaster.notifyFailure(
          err?.response?.status === 422
            ? "Couldn't find enough about this organization to write a bio. Check the website, or write one."
            : 'The bio could not be generated. Try again.'
        )
      })
  }

  const undoGenerateBio = () => {
    setProfile(current => (current ? { ...current, bio: bioBeforeGenerate } : current))
    setBioStatus('idle')
  }

  // Forced because the brief was built from the profile just replaced, however recently.
  // A customer with no brief yet will get one built from the new profile on its next view.
  const rebuildHealthCheckBrief = (teamID: string) => {
    setRegeneration('running')
    axios
      .post(`/api/teams/${teamID}/customers/${customerID}/briefs/health-check/rebuild?force=true`)
      .then(() => setRegeneration('complete'))
      .catch(err => setRegeneration(err?.response?.status === 404 ? 'idle' : 'failed'))
  }

  useEffect(() => {
    const controller = new AbortController()
    const teamID = defaultAuthHandler.getTeamID()

    setLoading(true)
    setError(null)

    const request = axios.get<CustomerProfile>(`/api/teams/${teamID}/customers/${customerID}/profile`, {
      signal: controller.signal,
    })

    if (request && typeof request.then === 'function') {
      request
        .then(response => {
          setProfile(response.data)
          setLoading(false)
        })
        .catch(err => {
          if (!axios.isCancel(err)) {
            setError(err.message || 'An error occurred while fetching the customer profile')
            setLoading(false)
          }
        })
    } else {
      setLoading(false)
    }

    return () => {
      controller.abort()
    }
  }, [customerID])

  if (loading) {
    return (
      <div className={`empty loading ${styles.loadingContainer}`} data-testid='loading-dots'>
        <div className={styles.loadingDotsWrapper}>
          <LoadingDots />
        </div>
        <div className={styles.loadingText}>
          <h4>Building your insights...</h4>
          Grab a quick sip
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`alert error ${styles.alertFlex}`} data-testid='profile-error'>
        <div>Failed to load customer profile: {error}</div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className={`alert error ${styles.alertFlex}`} data-testid='profile-empty'>
        <div>No customer profile found.</div>
      </div>
    )
  }

  return (
    <div data-testid='intel-tab'>
      <div className={styles.surfaceMb}>
        {/* Section 1: Organization Profile */}
        <div className={styles.intelSectionRow}>
          <div className={styles.intelInfoColumn}>
            <h2>
              <i className='icon thirtytwo flex-noshrink'>
                <NotesIcon />
              </i>
              Organization profile
            </h2>

            <p>
              <strong>Profile and context is used to inform the AI insights.</strong> The more information you provide
              here, the better. Try to focus on information that is not readily available on the web, including company
              values, internal policies, key stakeholder names. Do not put private information such as phone numbers.
            </p>
          </div>

          <div className={styles.intelFormColumn}>
            <div className={`${styles.surfaceField}`}>
              <label htmlFor='intel-name'>Name</label>
              <div className='flex flex-nowrap'>
                <input
                  id='intel-name'
                  type='text'
                  value={profile.name || ''}
                  onChange={e => handleFieldChange('name', e.target.value)}
                />
              </div>
            </div>

            <div className={`${styles.surfaceField}`}>
              <label htmlFor='intel-website'>Website</label>
              <div className='flex flex-nowrap'>
                <input
                  id='intel-website'
                  type='text'
                  inputMode='url'
                  value={profile.website || ''}
                  onChange={e => handleFieldChange('website', e.target.value)}
                  {...fieldProps('website', 'intel-website')}
                />
              </div>
              <FieldError field='website' id='intel-website' />
            </div>

            <div className={`${styles.surfaceField}`}>
              <label htmlFor='intel-industry'>Industry</label>
              <div className='flex flex-nowrap'>
                <input
                  id='intel-industry'
                  type='text'
                  value={profile.industry || ''}
                  onChange={e => handleFieldChange('industry', e.target.value)}
                />
              </div>
            </div>

            <div className={`${styles.surfaceField}`}>
              <label htmlFor='intel-location'>Location</label>
              <div className='flex flex-nowrap'>
                <input
                  id='intel-location'
                  type='text'
                  value={profile.location || ''}
                  onChange={e => handleFieldChange('location', e.target.value)}
                />
              </div>
            </div>

            <div className={`${styles.surfaceField}`}>
              <label htmlFor='intel-other-locations'>Other locations</label>
              <div className='flex flex-nowrap'>
                <input
                  id='intel-other-locations'
                  type='text'
                  value={profile.otherLocations ? profile.otherLocations.join(', ') : ''}
                  onChange={e =>
                    handleFieldChange(
                      'otherLocations',
                      e.target.value.split(',').map(s => s.trim())
                    )
                  }
                />
              </div>
            </div>

            <div className={`${styles.surfaceField}`}>
              <label htmlFor='intel-year-founded'>Year founded</label>
              <div className='flex flex-nowrap'>
                <input
                  id='intel-year-founded'
                  type='text'
                  inputMode='numeric'
                  maxLength={4}
                  value={profile.yearFounded || ''}
                  onChange={e => handleFieldChange('yearFounded', e.target.value)}
                  {...fieldProps('yearFounded', 'intel-year-founded')}
                />
              </div>
              <FieldError field='yearFounded' id='intel-year-founded' />
            </div>

            <div className={`${styles.surfaceField}`}>
              <label htmlFor='intel-employees'>Number of employees</label>
              <div className='flex flex-nowrap'>
                <input
                  id='intel-employees'
                  type='text'
                  value={profile.numberOfEmployees || ''}
                  onChange={e => handleFieldChange('numberOfEmployees', e.target.value)}
                  {...fieldProps('numberOfEmployees', 'intel-employees')}
                />
              </div>
              <FieldError field='numberOfEmployees' id='intel-employees' />
            </div>

            <div className={styles.surfaceField}>
              <div className={styles.fieldLabel}>
                <label htmlFor='intel-company-values'>Company values</label>
                <small className={styles.fieldHelpText}>Separate values with commas or new lines.</small>
              </div>
              <div>
                <textarea
                  id='intel-company-values'
                  value={companyValuesText ?? (profile.companyValues || []).join(', ')}
                  onChange={e => {
                    setCompanyValuesText(e.target.value)
                    handleFieldChange('companyValues', parseCompanyValues(e.target.value))
                  }}
                  rows={3}
                  className={styles.textareaFull}
                />
              </div>
            </div>

            {/* where the customer is in their lifecycle: sets the current stage on the Briefs tab and the table */}
            <div className={`${styles.surfaceField} ${styles.noBorder}`}>
              <label id='intel-lifecycle-stage' htmlFor={`intel-stage-${stageOrDefault(profile.lifecycleStage)}`}>
                Lifecycle stage
              </label>
              <div>
                <LifecycleStageToggle
                  value={stageOrDefault(profile.lifecycleStage)}
                  onChange={value => handleFieldChange('lifecycleStage', value)}
                />
              </div>
            </div>
          </div>
        </div>

        <hr className={styles.divider} />

        {/* Section 2: AI Customization */}
        <div className={styles.intelSectionRow}>
          <div className={styles.intelInfoColumn}>
            <h2>
              <i className='icon thirtytwo flex-noshrink'>
                <KeyInsightsIcon />
              </i>
              AI customization
            </h2>
            <p>
              This content is used to position and create bespoke and customized summaries relevant to this customer.
            </p>
          </div>

          <div className={styles.intelFormColumn}>
            <div className={`${styles.surfaceField}`}>
              <div className={styles.fieldLabel}>
                <label htmlFor='intel-bio'>Organization bio </label>
                <small className={styles.fieldHelpText}>
                  Used to customize Briefs. Initially auto-generated, you can edit or add any details you want to
                  further customize output.
                </small>
                <div className={styles.bioActions}>
                  <button
                    type='button'
                    className={`btn sml secondary ${bioStatus === 'generating' ? 'loading' : ''}`}
                    onClick={generateBio}
                    disabled={bioStatus === 'generating'}
                  >
                    <LoadingDots />
                    <span className='text'>{profile.bio ? 'Regenerate from website' : 'Generate from website'}</span>
                  </button>
                  {bioStatus === 'generating' && (
                    <span className={styles.bioStatus} role='status'>
                      Reading their website…
                    </span>
                  )}
                  {bioStatus === 'generated' && (
                    <span className={styles.bioStatus} role='status'>
                      Drafted from their website. Review it, then Update to save.{' '}
                      <button type='button' className={styles.bioUndo} onClick={undoGenerateBio}>
                        Undo
                      </button>
                    </span>
                  )}
                </div>
              </div>
              <div>
                <textarea
                  id='intel-bio'
                  value={profile.bio || ''}
                  onChange={e => {
                    handleFieldChange('bio', e.target.value)
                    if (bioStatus === 'generated') setBioStatus('idle')
                  }}
                  rows={4}
                  className={styles.textareaFull}
                  aria-busy={bioStatus === 'generating' || undefined}
                />
              </div>
            </div>

            <div className={`${styles.surfaceField} ${styles.noBorder}`}>
              <div className={styles.fieldLabel}>
                <label htmlFor='intel-notes'>Notes </label>
                <small className={styles.fieldHelpText}>
                  Consider brain-dumping everything you know about this customer. The more context, the more likely the
                  system is to suggest relevant information. eg. the customer prides itself on community engagement and
                  ESG.
                </small>
              </div>
              <div>
                <textarea
                  id='intel-notes'
                  value={profile.notes || ''}
                  onChange={e => handleFieldChange('notes', e.target.value)}
                  rows={4}
                  className={styles.textareaFull}
                />
              </div>
            </div>
          </div>
        </div>

        <hr className={styles.divider} />

        {/* Section 3: Value Estimation */}
        <div className={styles.intelSectionRow}>
          <div className={styles.intelInfoColumn}>
            <h2>
              <i className='icon thirtytwo flex-noshrink'>
                <CurrencyIcon />
              </i>
              Value estimation
            </h2>
            <FleetValueFormula variant={options.fleetValueFormula === 'text' ? 'text' : 'formula'} />
          </div>

          <div className={styles.intelFormColumn}>
            <div className={`${styles.surfaceField}`}>
              <label htmlFor='intel-currency'>Currency</label>
              <div className='flex flex-nowrap'>
                <input
                  id='intel-currency'
                  type='text'
                  value={profile.currencyCode || ''}
                  onChange={e => handleFieldChange('currencyCode', e.target.value)}
                  placeholder='e.g. AUD'
                  autoComplete='off'
                  spellCheck={false}
                  {...fieldProps('currencyCode', 'intel-currency')}
                />
              </div>
              <FieldError field='currencyCode' id='intel-currency' />
            </div>

            <div className={`${styles.surfaceField}`}>
              <label htmlFor='intel-value-per-device'>Average value per device (per annum)</label>
              <div className='flex flex-nowrap'>
                <div className={styles.amountField}>
                  <input
                    id='intel-value-per-device'
                    type='text'
                    inputMode='decimal'
                    value={profile.deviceValue ?? ''}
                    onChange={e => handleFieldChange('deviceValue', e.target.value)}
                    {...fieldProps('deviceValue', 'intel-value-per-device')}
                  />
                  <span
                    className={`${styles.currencyAffix} ${profile.currencyCode ? '' : styles.currencyAffixEmpty}`}
                    aria-hidden='true'
                  >
                    {profile.currencyCode?.trim() || 'Currency'}
                  </span>
                </div>
              </div>
              <FieldError field='deviceValue' id='intel-value-per-device' />
            </div>

            <div className={`${styles.surfaceField}`}>
              <label htmlFor='intel-cost-per-bw-page'>Cost per B&W page printed</label>
              <div className='flex flex-nowrap'>
                <div className={styles.amountField}>
                  <input
                    id='intel-cost-per-bw-page'
                    type='text'
                    inputMode='decimal'
                    value={profile.pageCostBW ?? ''}
                    onChange={e => handleFieldChange('pageCostBW', e.target.value)}
                    {...fieldProps('pageCostBW', 'intel-cost-per-bw-page')}
                  />
                  <span
                    className={`${styles.currencyAffix} ${profile.currencyCode ? '' : styles.currencyAffixEmpty}`}
                    aria-hidden='true'
                  >
                    {profile.currencyCode?.trim() || 'Currency'}
                  </span>
                </div>
              </div>
              <FieldError field='pageCostBW' id='intel-cost-per-bw-page' />
            </div>

            <div className={`${styles.surfaceField} ${styles.noBorder}`}>
              <label htmlFor='intel-cost-per-color-page'>Cost per Color page printed</label>
              <div className='flex flex-nowrap'>
                <div className={styles.amountField}>
                  <input
                    id='intel-cost-per-color-page'
                    type='text'
                    inputMode='decimal'
                    value={profile.pageCostColor ?? ''}
                    onChange={e => handleFieldChange('pageCostColor', e.target.value)}
                    {...fieldProps('pageCostColor', 'intel-cost-per-color-page')}
                  />
                  <span
                    className={`${styles.currencyAffix} ${profile.currencyCode ? '' : styles.currencyAffixEmpty}`}
                    aria-hidden='true'
                  >
                    {profile.currencyCode?.trim() || 'Currency'}
                  </span>
                </div>
              </div>
              <FieldError field='pageCostColor' id='intel-cost-per-color-page' />
            </div>
          </div>
        </div>

        <div className={styles.saveActions}>
          <button
            className={`btn ${saving ? 'loading' : ''} ${styles.btnMinWidth}`}
            onClick={handleSave}
            disabled={saving || regeneration === 'running'}
          >
            <LoadingDots />
            <span className='text'>Update</span>
          </button>
          {regeneration === 'running' && (
            <span className={`${styles.regenerationStatus} ${styles.regenerating}`}>Regenerating briefs…</span>
          )}
          {regeneration === 'complete' && (
            <span className={styles.regenerationStatus}>Brief regeneration complete</span>
          )}
          {regeneration === 'failed' && <span className={styles.regenerationStatus}>Brief regeneration failed</span>}
        </div>
      </div>
    </div>
  )
}

interface Props {
  teamID: string
  hosts: HostData[]
  hostStates: HostState[]
}

function isBriefDetailPath(pathname: string): boolean {
  return /\/briefs\/[^/]+$/.test(pathname) && !pathname.endsWith('/admin-tools')
}

export const CustomerDetail: FunctionComponent<Props> = props => {
  const { customerID } = useParams<{ customerID: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const host = props.hosts.find(h => h.id === customerID)

  useEffect(() => {
    if (customerID && customerID !== 'demo-baseline') {
      localStorage.setItem('last_visited_customer_id', customerID)
    }
  }, [customerID])

  if (!host) {
    return <Navigate to='/intelligence-hub' replace />
  }

  const hostState = props.hostStates.find(h => h.id === customerID)

  if (isBriefDetailPath(location.pathname)) {
    return (
      <div className={`auto ${styles.briefPadding}`}>
        <Routes>
          <Route path='briefs/*' element={<BriefsHub teamID={props.teamID} host={host} />} />
        </Routes>
      </div>
    )
  }

  const overviewPath = `/intelligence-hub/${host.id}`
  const isBriefsTab = location.pathname.startsWith(`${overviewPath}/briefs`)
  const isIntelTab = location.pathname.startsWith(`${overviewPath}/intel`)
  const hideSidebar = isBriefsTab || isIntelTab
  const canEditIntelligence = AuthRole.hasPermission('editIntelligence')

  return (
    <div className='tab' id='tab-intelligence-hub-customer'>
      <header>
        <div className='auto'>
          <div className='flex flex-ver'>
            <button
              onClick={() => navigate('/intelligence-hub')}
              className={healthStyles.backButton}
              aria-label='Back to Intelligence Hub'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
                className={healthStyles.iconSubdued}
              >
                <polyline points='15 18 9 12 15 6'></polyline>
              </svg>
            </button>
            <div className='flex-grow flex flex-ver'>
              <h1 className='push'>{host.name}</h1>
            </div>
          </div>
        </div>
        <div className='auto'>
          <div className='tabs'>
            <ul className='auto'>
              <RouteTabLink path={overviewPath}>Overview</RouteTabLink>
              <RouteTabLink path={`${overviewPath}/intel`}>Intel</RouteTabLink>
              <RouteTabLink path={`${overviewPath}/briefs`}>Briefs</RouteTabLink>
              {canEditIntelligence && <RouteTabLink path={`${overviewPath}/admin-tools`}>Admin tools</RouteTabLink>}
            </ul>
          </div>
        </div>
      </header>

      <div className='auto'>
        <div className={styles.splitRow}>
          {/* Hide Summary column when viewing Briefs or Intel tab */}
          {!hideSidebar && (
            <div className={styles.splitSidebar}>
              <CustomerDetailsPanel teamID={props.teamID} host={host} hostState={hostState} />
            </div>
          )}
          <div className={hideSidebar ? styles.splitContentFull : styles.splitContent}>
            <Routes>
              <Route index element={<OverviewTab teamID={props.teamID} host={host} />} />
              <Route path='intel' element={<IntelTab customerID={host.id} />} />
              <Route path='briefs/*' element={<BriefsHub teamID={props.teamID} host={host} />} />
              {canEditIntelligence && (
                <Route path='admin-tools' element={<AdminToolsTab teamID={props.teamID} customerID={host.id} />} />
              )}
              <Route path='*' element={<Navigate to='.' replace />} />
            </Routes>
          </div>
        </div>
      </div>

      {/* DEMO ONLY: Mount Panel */}
      <DemoOptionsPanel />
    </div>
  )
}
